import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { blogPosts } from "@/content/blogPosts";
import { SEO } from "@/components/seo/SEO";
import MarketingNav from "@/components/MarketingNav";

export default function Blog() {
  const featured = blogPosts[0];
  const rest = blogPosts.slice(1);

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#111]">
      <SEO
        title="Blog – Manifest"
        description="Tips and guides on home inventory, insurance claims, product recalls, return tracking, and protecting what you own."
        canonical="https://manifestme.me/blog"
        type="website"
      />

      {/* Nav */}
      <MarketingNav />

      <div className="mx-auto max-w-[960px] px-5 py-16 animate-fade-in">
        <h1 className="font-editorial text-[clamp(2rem,4vw,3rem)] leading-[1.1] mb-3">Blog</h1>
        <p className="text-[15px] text-[#6B7280] mb-12 max-w-[500px] leading-[1.6]">
          Guides on protecting what you own — insurance, recalls, returns, and everything in between.
        </p>

        {blogPosts.length === 0 ? (
          <div className="rounded-xl border border-[#E5E5E0] bg-white p-8 shadow-sm text-center">
            <p className="text-[#6B7280] text-sm">New articles coming soon.</p>
          </div>
        ) : (
          <>
            {/* Featured post */}
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                className="block rounded-2xl border border-[#E5E5E0] bg-white p-8 md:p-10 shadow-sm hover:shadow-md hover:border-[#ccc] transition-all group mb-8"
              >
                <span className="inline-block text-[11px] font-bold uppercase tracking-[0.08em] text-[#0F766E] bg-[#0F766E]/8 px-2.5 py-1 rounded-full mb-4">
                  {featured.category}
                </span>
                <h2 className="font-editorial text-[clamp(1.3rem,3vw,1.75rem)] tracking-tight leading-[1.2] mb-3 group-hover:text-[#0F766E] transition-colors">
                  {featured.title}
                </h2>
                <p className="text-[14px] text-[#6B7280] leading-[1.7] mb-4 max-w-[600px]">
                  {featured.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#0F766E]">
                    Read article <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                  <span className="flex items-center gap-1.5 text-[12px] text-[#9CA3AF]">
                    <Clock className="h-3.5 w-3.5" />
                    {featured.readTime}
                  </span>
                </div>
              </Link>
            )}

            {/* Grid */}
            {rest.length > 0 && (
              <div className="grid gap-5 sm:grid-cols-2">
                {rest.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="flex flex-col rounded-xl border border-[#E5E5E0] bg-white p-6 shadow-sm hover:shadow-md hover:border-[#ccc] transition-all group"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#0F766E] bg-[#0F766E]/8 px-2 py-0.5 rounded-full">
                        {post.category}
                      </span>
                      <span className="ml-auto flex items-center gap-1 text-[11px] text-[#9CA3AF]">
                        <Clock className="h-3 w-3" />
                        {post.readTime}
                      </span>
                    </div>
                    <h2 className="font-editorial text-[1.05rem] tracking-tight leading-[1.25] mb-2 group-hover:text-[#0F766E] transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-[13px] text-[#6B7280] leading-[1.6] mb-4 flex-1">
                      {post.description}
                    </p>
                    <span className="inline-flex items-center gap-1 text-[13px] font-medium text-[#0F766E] mt-auto">
                      Read article <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-[#E5E5E0] bg-[#FAFAF7]">
        <div className="mx-auto max-w-[960px] px-5 py-8 flex items-center justify-between text-[12px] text-[#9CA3AF]">
          <span>© {new Date().getFullYear()} Manifest</span>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-[#6B7280]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#6B7280]">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
